/**
 * How the Connectors modal groups the registry: a plain heading per group,
 * groups in this order, server order inside a group. A category the client
 * does not know yet lands in "More", last, so a new server entry never
 * disappears. CATEGORY_WORDS widen search to what a connector is for
 * ("email", "bookings"), not just its name; they are per category so
 * "email" finds Resend and not Cal.com, its neighbour in the same group.
 */
export interface ConnectorGroup {
  id: string;
  label: string;
  categories: (string | null)[];
}

export const CONNECTOR_GROUPS: ConnectorGroup[] = [
  { id: 'video', label: 'Video', categories: [null] },
  { id: 'payments', label: 'Payments', categories: ['payments'] },
  // D-13: the order a small business reaches for them.
  { id: 'customers', label: 'Email, bookings and forms', categories: ['email', 'scheduling', 'forms'] },
  { id: 'work', label: 'Projects and docs', categories: ['project', 'docs'] },
  { id: 'data', label: 'Data and design', categories: ['database', 'crm', 'files', 'design'] },
  { id: 'growth', label: 'Analytics and automation', categories: ['analytics', 'automation'] },
  { id: 'code', label: 'Code and errors', categories: ['code', 'monitoring'] },
];

export const MORE_GROUP: ConnectorGroup = { id: 'more', label: 'More', categories: [] };

const CATEGORY_WORDS: Record<string, string> = {
  payments: 'payments money checkout invoices orders',
  database: 'data database tables records spreadsheet',
  crm: 'crm customers contacts companies deals sales pipeline',
  files: 'files folders photos images documents storage',
  code: 'code repos repositories pull requests commits',
  docs: 'docs notes pages wiki',
  project: 'issues tasks projects tickets roadmap',
  design: 'design brand assets images logo',
  email: 'email newsletter marketing lists campaigns subscribers',
  scheduling: 'bookings calendar appointments scheduling',
  forms: 'forms surveys quiz questionnaire responses',
  analytics: 'analytics visitors traffic events funnels experiments flags',
  monitoring: 'errors bugs crashes monitoring',
  automation: 'automation workflows',
};

/** Extra words a search for this category should match. */
export function searchWordsFor(category: string | null | undefined): string {
  if (!category) return 'video avatar';
  return CATEGORY_WORDS[category] || '';
}

export function groupFor(category: string | null | undefined): ConnectorGroup {
  const c = category || null;
  return CONNECTOR_GROUPS.find((g) => g.categories.includes(c)) || MORE_GROUP;
}

/** Sort key: group order, then category order inside the group. */
export function categoryRank(category: string | null | undefined): number {
  const g = groupFor(category);
  const gi = g === MORE_GROUP ? CONNECTOR_GROUPS.length : CONNECTOR_GROUPS.indexOf(g);
  const ci = Math.max(0, g.categories.indexOf(category || null));
  return gi * 100 + ci;
}
